'use client';

import { IMediaItem, UploadStatus, isBlobUrl } from '@/lib/types/media/core';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface MediaUploadContextType {
  uploadState: Record<string, UploadStatus>;
  updateUploadState: (mediaId: string, status: UploadStatus) => void;
  clearUploadState: (mediaId: string) => void;
  hasActiveUploads: boolean;
  getMediaUploadStatus: (mediaId: string) => UploadStatus | null;
  isMediaUploading: (media: IMediaItem | { url: string; id?: string }) => boolean;
  validateMediaItems: (mediaItems: IMediaItem[]) => { isValid: boolean; message?: string };
}

const MediaUploadContext = createContext<MediaUploadContextType | null>(null);

export function MediaUploadProvider({ children }: { children: React.ReactNode }) {
  const [uploadState, setUploadState] = useState<Record<string, UploadStatus>>({});

  // Update upload state for a specific media item
  const updateUploadState = useCallback((mediaId: string, status: UploadStatus) => {
    // Only update if state has changed to prevent unnecessary re-renders
    setUploadState(prev => {
      const current = prev[mediaId];
      // Skip update if state is identical
      if (current &&
          current.status === status.status &&
          current.progress === status.progress) {
        return prev;
      }
      return { ...prev, [mediaId]: status };
    });
  }, []);

  // Remove upload state for a specific media item
  const clearUploadState = useCallback((mediaId: string) => {
    // Only clear if the media ID exists in the state
    setUploadState(prev => {
      if (!prev[mediaId]) return prev;
      const { [mediaId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Check if there are any active uploads
  const hasActiveUploads = Object.values(uploadState).some(
    status => status.status === 'uploading' || status.status === 'processing'
  );

  // Get upload status for a specific media item
  const getMediaUploadStatus = useCallback((mediaId: string): UploadStatus | null => {
    return uploadState[mediaId] || null;
  }, [uploadState]);

  // Check if a media item is currently uploading
  const isMediaUploading = useCallback((media: IMediaItem | { url: string; id?: string }): boolean => {
    // Check if the media has a blob URL (which means it's still being uploaded)
    if (media.url && isBlobUrl(media.url)) {
      return true;
    }

    // Check if media has an ID and is in upload state
    if (media.id && uploadState[media.id]) {
      const status = uploadState[media.id].status;
      return status === 'uploading' || status === 'processing';
    }

    return false;
  }, [uploadState]);

  // Validate an array of media items for upload completion
  const validateMediaItems = useCallback((mediaItems: IMediaItem[]): { isValid: boolean; message?: string } => {
    if (!mediaItems || mediaItems.length === 0) {
      return { isValid: true };
    }

    // Check if any media item has a blob URL
    const pendingBlobItems = mediaItems.filter(item => item.url && isBlobUrl(item.url));
    if (pendingBlobItems.length > 0) {
      return {
        isValid: false,
        message: 'Some media items are still being uploaded'
      };
    }

    // Check if any media item is in uploading state
    const uploadingItems = mediaItems.filter(item =>
      item.id && uploadState[item.id] &&
      (uploadState[item.id].status === 'uploading' || uploadState[item.id].status === 'processing')
    );

    if (uploadingItems.length > 0) {
      return {
        isValid: false,
        message: 'Some media items are still being processed'
      };
    }

    return { isValid: true };
  }, [uploadState]);

  // Clean up upload states when component unmounts
  useEffect(() => {
    return () => {
      // Any cleanup needed for upload tracking
    };
  }, []);

  const value = {
    uploadState,
    updateUploadState,
    clearUploadState,
    hasActiveUploads,
    getMediaUploadStatus,
    isMediaUploading,
    validateMediaItems
  };

  return (
    <MediaUploadContext.Provider value={value}>
      {children}
    </MediaUploadContext.Provider>
  );
}

export function useMediaUpload() {
  const context = useContext(MediaUploadContext);
  if (!context) {
    throw new Error('useMediaUpload must be used within a MediaUploadProvider');
  }
  return context;
}
